import styled from '@emotion/styled';
import Container from '@ep/components/Container';
import ContentLightBox from '@ep/components/ContentLightBox';
import Media from '@ep/components/Media';
import { H1 } from '@ep/templates/ProductPage';
import theme from '@ep/theme';
import { Gcms_Asset } from '@ep/types/gatsbyTypes';
import { Gcms_RichText } from '@ep/types/gatsbyTypes';
import getHtmlProp from '@ep/utils/getHtmlProp';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Typography } from '@mui/material';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import React, { useState } from 'react';

const StyledContainer = styled(Container)`
  background-color: ${theme.colors.lightGray};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(100, 1fr);
  grid-template-rows: repeat(100, 1fr);
  width: 100%;
  aspect-ratio: 25/16;
`;

const BackgroundImage = styled(Media)`
  width: 100%;
  position: relative;
  grid-column: 1 / span 100;
  grid-row: 1 / span 100;
  aspect-ratio: 25/16;
`;

const ItemContainer = styled.div`
  position: relative;
  grid-column: ${props => props.x} / span 1;
  grid-row: ${props => props.y} / span 1;
  height: 5px;
  width: 5px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const Detach = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TextBox = styled.div`
  background-color: ${theme.colors.mediumBlue85};
  width: 17rem;
  color: ${theme.colors.white};
  text-align: center;
  padding: 0.5rem;
  transition: 0.5s;
  margin-bottom: 0.5rem;
  z-index: 2;
`;

const Line = styled.div`
  width: ${(props: VideoItemProp) => (props.pinSize ? '0.25rem' : '0.12rem')};
  height: 3rem;
  background-color: ${(props: VideoItemProp) =>
    !props.pinColor ? theme.colors.oceanBlue : props.pinColor.hex};
  ${theme.mediaQueries.tablet} {
    height: 2rem;
  }
  ${theme.mediaQueries.mobile} {
    height: 1rem;
  }
`;

const Circle = styled.div`
  height: ${(props: VideoItemProp) => (props.pinSize ? '3rem' : '1.8rem')};
  width: ${(props: VideoItemProp) => (props.pinSize ? '3rem' : '1.8rem')};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props: VideoItemProp) =>
    !props.pinColor ? theme.colors.oceanBlue : props.pinColor.hex};
  border-radius: 50%;
  cursor: pointer;
  ${theme.mediaQueries.tablet} {
    height: 1.9rem;
    width: 1.9rem;
  }
  ${theme.mediaQueries.mobile} {
    height: 1.5rem;
    width: 1.5rem;
  }
  transition: 0.25s;
  transform: rotate(180deg);
  &:hover {
    transition: 0.25s;
    transform: scale(1.1) rotate(0deg);
    background-color: ${theme.colors.mantisGreen};
  }
  &:hover + ${Line} {
    transition: 0.25s;
    background-color: ${theme.colors.mantisGreen};
  }
`;

const StyledAddIcon = styled(AddIcon)`
  height: 2rem;
  width: 2rem;
  ${theme.mediaQueries.mobile} {
    height: 1.5rem;
    width: 1.5rem;
  }
`;

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
  ${theme.mediaQueries.mobile} {
    justify-content: center;
    align-items: center;
  }
`;

const CloseModal = styled.div`
  position: relative;
  z-index: 3;
`;

const ModalContainer = styled(Container)`
  max-height: 95vh;
  width: 48rem;
  background-color: ${theme.colors.white};
  outline: none;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  ${theme.mediaQueries.tablet} {
    width: 40rem;
    margin: 0;
  }
  ${theme.mediaQueries.mobile} {
    width: 24rem;
    margin: 0;
  }
  p {
    text-align: center;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
`;

const ModalTitle = styled.div`
  font-size: 1.2em;
  font-weight: bold;
  padding-left: 2.5rem;
  text-align: center;
  width: 100%;
`;

const VideoWrapper = styled.div`
  width: 100%;
  min-width: 60rem;
  max-width: 82rem;
  margin: 0 auto;
  margin-top: ${(props: VideoWrapperProp) =>
    props.hasVideo ? '-4rem' : '-5rem'};
`;

const StyledMedia = styled(Media)`
  img {
    max-height: 30rem;
    width: 48rem;
    border-radius: 1rem 1rem 0 0;
  }
  ${theme.mediaQueries.tablet} {
    img {
      width: 40rem;
    }
  }
  ${theme.mediaQueries.mobile} {
    img {
      width: 24rem;
    }
  }
`;
const CaptionWrapper = styled.div`
  overflow-y: auto;
  max-height: 30rem;
  margin: 1rem;
`;

const Main = styled.div`
  p {
    margin-bottom: 0.5rem;
    :empty {
      height: 1rem;
    }
  }

  a:link,
  a:hover,
  a:visited {
    color: ${theme.colors.epLinkBlue};
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }

  h2 {
    color: ${theme.colors.duskBlue};
    margin-bottom: 1.2rem;
    padding-top: 2rem;
  }

  strong {
    color: ${theme.colors.oceanBlue};
  }

  ul {
    padding: 0;
    margin: 0;
    li {
      list-style: none;
      padding-left: 2rem;
      position: relative;
      &:before {
        content: '>';
        position: absolute;
        left: 0;
        top: 0;
        color: ${theme.colors.oceanBlue};
      }
    }
  }

  ol {
    padding: 0;
    margin: 2rem;
    li {
      list-style: none;
      padding-left: 2rem;
      position: relative;
    }
  }

  li {
    margin-bottom: 0.5rem;
  }

  table {
    width: 100%;
    td {
      width: 20rem;
      img {
        display: block;
        max-width: 16rem;
        margin: 0 auto;
      }
    }
  }
`;

export interface ColorProps {
  hex: string;
}

export interface VideoItemProp {
  title: string;
  showTitle: boolean;
  xCoordinate: string;
  yCoordinate: string;
  setOpen: (state: boolean) => void;
  itemIndex: number;
  setModalIndex: (index: number) => void;
  media?: Gcms_Asset;
  mediaCaption?: string;
  pinSize: boolean;
  pinColor: ColorProps;
  mediaCaptionContent?: Gcms_RichText | null;
}
export interface VideoGridProp {
  title: string;
  background: Gcms_Asset;
  items: VideoItemProp[];
}

export interface VideoWrapperProp {
  hasVideo: boolean;
}

const VideoItem = ({
  title,
  showTitle,
  xCoordinate,
  yCoordinate,
  setOpen,
  setModalIndex,
  itemIndex,
  pinSize,
  pinColor,
  mediaCaptionContent,
}: VideoItemProp) => {
  // const [open, setOpen] = useState<Boolean>(false);
  const [hover, setHover] = useState<Boolean>(false);

  const opacity = hover ? { opacity: '1' } : { opacity: '0' };

  const onMouseEnter = e => {
    if (showTitle) {
      setHover(true);
    }
  };

  const onMouseLeave = e => {
    if (showTitle) {
      setHover(false);
    }
  };

  const HandleClick = (index: number, dialogTitle: string) => {
    // dataLayer.push({
    //   event:
    //     'Page Interaction - user clicked on video grid item -  ' + dialogTitle,
    // });
    setModalIndex(index);
    setOpen(true);
  };

  return (
    <ItemContainer x={xCoordinate} y={yCoordinate}>
      <Detach>
        {showTitle && (
          <TextBox id="text" style={opacity}>
            {title}
          </TextBox>
        )}
        <Circle
          id="circle"
          onClick={() => HandleClick(itemIndex, title)}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          pinSize={pinSize}
          pinColor={pinColor}
        >
          <StyledAddIcon htmlColor={theme.colors.white} />
        </Circle>
        <Line data-comp="lineDiv" pinSize={pinSize} pinColor={pinColor} />
      </Detach>
    </ItemContainer>
  );
};

const VideoGridModule = ({ title, background, items }: VideoGridProp) => {
  const [open, setOpen] = useState<boolean>(false);
  const [modalIndex, setModalIndex] = useState<number>(0);

  return (
    <div style={{ backgroundColor: theme.colors.lightGray }}>
      <StyledContainer>
        <H1
          style={{
            textAlign: 'center',
            padding: '1rem 0',
            margin: '0',
          }}
        >
          {title}
        </H1>
        <Grid>
          <BackgroundImage media={background} />
          {items.map((item: VideoItemProp, index: number) => (
            <VideoItem
              key={item.title}
              title={item.title}
              xCoordinate={item.xCoordinate}
              yCoordinate={item.yCoordinate}
              setOpen={setOpen}
              itemIndex={index}
              setModalIndex={setModalIndex}
              pinSize={item.pinSize}
              pinColor={item.pinColor}
            />
          ))}
        </Grid>
        <StyledModal open={open} onClose={() => setOpen(false)}>
          <ModalContainer flexStyle={Boolean(items[modalIndex].media)}>
            <ModalHeader>
              {!items[modalIndex].media && (
                <ModalTitle>{items[modalIndex].title}</ModalTitle>
              )}
              <CloseModal>
                <IconButton
                  aria-label="close"
                  onClick={() => setOpen(false)}
                  style={{ backgroundColor: theme.colors.white85 }}
                >
                  <CloseIcon htmlColor={theme.colors.black} />
                </IconButton>
              </CloseModal>
            </ModalHeader>
            <div>
              {items[modalIndex].media && (
                <VideoWrapper
                  hasVideo={Boolean(items[modalIndex].media.vidyardLink)}
                >
                  <StyledMedia
                    media={
                      {
                        url: items[modalIndex].media.url,
                        vidyardLink: items[modalIndex].media.vidyardLink,
                      } as Gcms_Asset
                    }
                  />
                </VideoWrapper>
              )}
              {items[modalIndex].media && (
                <Typography
                  style={{
                    fontSize: '1.3rem',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    paddingTop: '1rem',
                    paddingLeft: '1rem',
                    paddingRight: '1rem',
                  }}
                >
                  {items[modalIndex].title}
                </Typography>
              )}
              {items[modalIndex].mediaCaption && (
                <CaptionWrapper>
                  <Typography
                    style={{ fontSize: '1.2rem', textAlign: 'center' }}
                  >
                    {items[modalIndex].mediaCaption}
                  </Typography>
                </CaptionWrapper>
              )}
              {items[modalIndex].mediaCaptionContent && (
                <CaptionWrapper>
                  <Main
                    {...getHtmlProp(items[modalIndex].mediaCaptionContent.html)}
                  />
                </CaptionWrapper>
              )}
            </div>
          </ModalContainer>
        </StyledModal>
      </StyledContainer>
    </div>
  );
};

export default VideoGridModule;
